import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import { PictureInPictureModeProvider } from '@/context/InvoiceReports';
import { useGetOfficeInvoiceReportSettings } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import type { InvoiceReportsSearchQuery } from '@/features/invoice_reports/types';
import { buildOrderByArray } from '@/features/invoice_reports/utils/buildOrderByArray';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo } from 'react';
import { Search } from '../Search';
import { InvoiceReportsButtonGroup } from './ButtonGroups';
import {
  useInvoiceReportsFormInitial,
  useInvoiceReportsHandleSearchQuery,
} from './hooks';
import { useContainerHeight } from './hooks/useContainerHeight';
import styles from './List.module.scss';
import { useInvoiceReportsSearchData } from './Table/hook';
import { InvoiceReportsTableView } from './Table/Table';

const cx = classnames.bind(styles);

export const InvoiceReports: FC = memo(() => {
  const { data: officeInvoiceReportSettings } =
    useGetOfficeInvoiceReportSettings();
  // When API errors, default to false (proxy OFF) so ProxyOffButtonGroup is shown
  const canAgentEntry =
    officeInvoiceReportSettings?.data?.office_member_setting?.can_agent_entry ??
    false;
  const reportTypeId =
    officeInvoiceReportSettings?.data?.invoice_report_type?.id;

  const defaultValues = useInvoiceReportsFormInitial(canAgentEntry);
  const { searchQuery, onSearch, handleMultiColumnSort, sortOrders } =
    useInvoiceReportsHandleSearchQuery(defaultValues);

  // Use custom hook for container height
  const { containerRef, containerStyle } = useContainerHeight();

  // Memoize table query with extracted helper function
  const tableQuery = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, sort_fields, sort_orders, ...searchParams } = searchQuery;
    const order_by = buildOrderByArray(sortOrders);

    return {
      query: searchParams,
      page,
      order_by,
    };
  }, [searchQuery, sortOrders]);

  const [
    { setError },
    ErrorContextHolder,
    ErrorInlineNotificationProvider,
    onClearMessage,
  ] = useErrorInlineNotification();

  const { data, pagination, refetch, isFetching } = useInvoiceReportsSearchData(
    tableQuery,
    setError
  );

  // Stable search handler
  const onHandleSearch = useCallback(
    (searchData: InvoiceReportsSearchQuery) => {
      onClearMessage();
      onSearch(searchData, refetch);
    },
    [onSearch, onClearMessage, refetch]
  );

  // Memoize container class name
  const containerClassName = cx(styles['container']);
  const notificationClassName = cx(styles['inline-notification-wrap']);
  const isApplyMbToClassName = useMemo(() => {
    return (pagination.maxPage === 1 || data.length === 0) && !isFetching;
  }, [pagination.maxPage, data.length, isFetching]);

  return (
    <PictureInPictureModeProvider>
      <div
        className={containerClassName}
        ref={containerRef}
        style={containerStyle}
      >
        <ErrorInlineNotificationProvider value={setError}>
          <div className={notificationClassName}>{ErrorContextHolder}</div>
          <Search
            defaultValues={defaultValues}
            onSearch={onHandleSearch}
            searchQuery={searchQuery}
            isLoading={isFetching}
            officeInvoiceReportSettings={officeInvoiceReportSettings?.data}
          />
          <InvoiceReportsButtonGroup
            canAgentEntry={canAgentEntry}
            reportTypeId={reportTypeId}
            containerClassName={isApplyMbToClassName ? cx(['header_mb']) : ''}
          />
          <InvoiceReportsTableView
            searchQuery={searchQuery}
            onSearch={onHandleSearch}
            onMultiColumnSort={handleMultiColumnSort}
            sortOrders={sortOrders}
            query={tableQuery}
            data={data}
            pagination={pagination}
            isFetching={isFetching ?? false}
            canAgentEntry={canAgentEntry}
          />
        </ErrorInlineNotificationProvider>
      </div>
    </PictureInPictureModeProvider>
  );
});

InvoiceReports.displayName = 'InvoiceReports';
